/**
 * The in-season half of «Scegli un run» (`docs/adr/9033`): the runs of ONE kind, described for
 * the shared `ChooseRunState`, and — since DEC-99 (`docs/adr/9039`) — the list a distribute or
 * rebalance page opens its newest run from.
 *
 * The run directory (`GET /inseason/runs`) lists a cycle's distribute, rebalance and price runs
 * alike and publishes no run kind (F4; `CycleRunPicker` asks bata-platform for one). A page that
 * reads proposals offered a price run, and the planner opened an empty page. So a run is offered
 * on a page only when it holds that page's kind of line: a distribute run holds proposals, a
 * rebalance run transfers, a price run price steps.
 *
 * **How the kind is read — from the run's own row, with no read of the queues at all.** Until
 * 2026-10-02 each run's kind was read from its digest; then, until 2026-10-13, from one bounded
 * page of the page's own queue (`/proposals`, `/transfers`, `/price-actions` with a `limit`) —
 * up to 24 requests on every distribute, rebalance and price page, each a query on the server.
 * bata-platform now says it on the row (ADR 6176): `proposal_lines`, `transfer_lines` and
 * `price_actions` count the run's lines of each kind, and `proposals_superseded_by_run_id`,
 * `transfers_superseded_by_run_id` and `price_actions_superseded_by_run_id` name the run that
 * took each kind over. The directory is one request the page already makes, so the kind of every
 * run costs nothing, and a price run's volume is exact — never «more than».
 *
 * One limit, stated rather than hidden: only the newest {@link KIND_PROBE_RUNS} runs of the
 * directory are considered, as before (the list is a gate, not an archive). An older run is
 * still reachable by link and by the picker at the head of the page once a run is open.
 *
 * **A superseded run is not one to open by itself** (audit 2, 2026-10-12). A newer tick supersedes
 * a run's lines of a kind (bata-platform ADR 6048), so the row names it for that kind.
 * `useKindRuns().current` is the runs among `data` that still own their lines: those are what a
 * page opens on by itself («the newest run with transfers»). `data` still lists the superseded
 * ones, for the «Scegli un run» gate, which is only drawn when no run is current: a planner may
 * read history, but a page never opens on a run whose decisions now belong elsewhere and calls it
 * the newest.
 */

import { useTranslation } from 'react-i18next';

import { inseasonListRuns } from '@/api/generated';
import type { CycleRunSummary } from '@/api/generated';
import { useApiCollection } from '@/api';
import { runName, type ChooseRunOption } from '@/planning';

/** The kind of in-season run a page reads. */
export type InSeasonRunKind = 'distribute' | 'rebalance' | 'price';

/** How many of the directory's newest runs are considered for their kind. */
export const KIND_PROBE_RUNS = 24;

/** What a run's own row says of one kind: how many lines it holds, and who superseded them. */
export interface KindHolding {
  readonly lines: number;
  /** The run that superseded this one for the kind (ADR 6176); `null` when it still owns its lines. */
  readonly supersededBy: string | null;
}

/** The empty string and `undefined` are as absent as `null`: no run took the lines over. */
function supersessionOf(by: string | null | undefined): string | null {
  return by === undefined || by === null || by === '' ? null : by;
}

/** What a directory row says of the lines of `kind` — read off the row, never off a queue. Pure. */
export function holdingOf(kind: InSeasonRunKind, row: CycleRunSummary): KindHolding {
  switch (kind) {
    case 'distribute': return { lines: row.proposal_lines, supersededBy: supersessionOf(row.proposals_superseded_by_run_id) };
    case 'rebalance': return { lines: row.transfer_lines, supersededBy: supersessionOf(row.transfers_superseded_by_run_id) };
    case 'price': return { lines: row.price_actions, supersededBy: supersessionOf(row.price_actions_superseded_by_run_id) };
  }
}

/** Whether a newer run took over `row`'s lines of `kind` (ADR 6176) — a run a page never opens by itself. */
export function isSupersededFor(kind: InSeasonRunKind, row: CycleRunSummary): boolean {
  const by = holdingOf(kind, row).supersededBy;
  return by !== null && by !== row.run_id;
}

/** When a run was recorded: `recorded_at`, else `generated_at` (a platform before ADR 6034). */
export function recordedAt(row: CycleRunSummary): string {
  return (row as { readonly recorded_at?: string | null }).recorded_at ?? row.generated_at;
}

/** The directory, newest recorded first — the platform's order, stable for equal instants. */
export function newestFirst(rows: readonly CycleRunSummary[]): readonly CycleRunSummary[] {
  return [...rows].sort((a, b) => {
    const left = recordedAt(a);
    const right = recordedAt(b);
    return left < right ? 1 : left > right ? -1 : 0;
  });
}

/** What «Scegli un run» says of a run's volume: the price steps the row counts (exact). */
function volumeOf(kind: InSeasonRunKind, holding: KindHolding, t: (key: string, values: Record<string, unknown>) => string): string | null {
  if (kind !== 'price') return null;
  return t('inseason.chooseRun.volume.price', { count: holding.lines });
}

/**
 * The runs of `kind` among the newest {@link KIND_PROBE_RUNS}, newest first, described for
 * `ChooseRunState`. Nothing is requested here: the directory read is shared and cached, and the
 * kind is on its rows. With `enabled` false the list is empty (the caller is not asking).
 */
export function useKindRuns(kind: InSeasonRunKind, enabled = true): {
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly error: unknown;
  /** Every run among the newest that holds a line of the kind — superseded or not. */
  readonly data: readonly ChooseRunOption[] | undefined;
  /** The ids among `data` whose lines are not superseded, newest first: what a page may open on by itself. */
  readonly current: readonly string[] | undefined;
  readonly refetch: () => unknown;
} {
  const { t } = useTranslation();
  // The same read, and query key, as `CycleRunPicker` and `RunRequired`: one request.
  const directory = useApiCollection(inseasonListRuns, { query: {} });
  const considered = enabled ? newestFirst(directory.data ?? []).slice(0, KIND_PROBE_RUNS) : [];

  const superseded = new Set<string>();
  const data = directory.data === undefined ? undefined : considered.flatMap((row): ChooseRunOption[] => {
    const holding = holdingOf(kind, row);
    // A row that counts nothing (or no count at all) holds none of the kind.
    if (!(holding.lines > 0)) return [];
    if (holding.supersededBy !== null && holding.supersededBy !== row.run_id) superseded.add(row.run_id);
    return [{
      runId: row.run_id,
      label: runName(row, t),
      market: row.country_key,
      recordedAt: recordedAt(row),
      volume: volumeOf(kind, holding, t),
      state: t(row.stock_partition_is_fresh ? 'inseason.chooseRun.state.fresh' : 'inseason.run.picker.staleBadge'),
    }];
  });

  return {
    isPending: data === undefined && !directory.isError,
    isError: directory.isError,
    error: directory.error,
    data,
    current: data?.map((run) => run.runId).filter((runId) => !superseded.has(runId)),
    refetch: () => directory.refetch(),
  };
}
